import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import moment from "moment";
import { loadScoreDetail, clearScoreDetail, loadLibraryKpis } from "reducers/PredictiveScoring";
import { getScoreDetail, getAvailableKpis } from "selectors/PredictiveScoring";
import { trackScoreViewed, trackKpiToggle } from "utils/PredictiveScoringPendo";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import { DATE_AND_TIME_FORMAT } from "utils/constants";
import { getScoreColorHex, getScoreLabel as getSharedScoreLabel } from "utils/predictiveScoreTiers";

import "./PredictiveScorePanel.css";

const DISCLAIMER_TEXT = "Scores represent AI-modeled estimates; use them as a guide, not a guarantee of future outcomes.";
const KPI_TOOLTIP = "The KPI the predictive score is optimized for. Switch KPIs to see the score and insights for each.";
const SELECT_COMPONENTS = { IndicatorSeparator: () => null };

const SCORE_MAX = 100;
const GAUGE_SIZE = 160;
const GAUGE_STROKE_WIDTH = 10;
const HALF_DIVISOR = 2;

// Re-exported so the gauge (and its tests) keep a local name, but both color and label come from
// the single shared tier table (kept in sync with the engine's score bands).
export const getScoreColor = getScoreColorHex;
export const getScoreLabel = getSharedScoreLabel;

// The tier label ("Very Good") is title-cased for the gauge, but reads mid-sentence in the subtitle,
// so lower-case it and re-capitalize only the first letter → "Very good creative performance expected".
function sentenceCaseLabel(label) {
	if (!label) { return ""; }
	const lower = label.toLowerCase();
	return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// Score objects carry the raw engine key (e.g. "display-ctr"); the KPI config list carries the
// friendly displayName keyed by engineKeyDisplay/engineKeyVideo. Resolve the friendly name, falling
// back to the raw key when no matching config is loaded.
function resolveKpiName(engineKey, availableKpis) {
	const match = availableKpis.find(
		kpi => kpi.engineKeyDisplay === engineKey || kpi.engineKeyVideo === engineKey
	);
	return match ? match.displayName : engineKey;
}

function ScoreGauge({ score }) {
	const radius = (GAUGE_SIZE - GAUGE_STROKE_WIDTH) / HALF_DIVISOR;
	const circumference = HALF_DIVISOR * Math.PI * radius;
	const progress = (score / SCORE_MAX) * circumference;
	const color = getScoreColor(score);
	const label = getScoreLabel(score);
	const center = GAUGE_SIZE / HALF_DIVISOR;

	return (
		<div className="ps-gauge">
			<svg width={GAUGE_SIZE} height={GAUGE_SIZE} viewBox={`0 0 ${GAUGE_SIZE} ${GAUGE_SIZE}`}>
				<circle
					cx={center}
					cy={center}
					r={radius}
					fill="none"
					stroke="#eee"
					strokeWidth={GAUGE_STROKE_WIDTH}
				/>
				<circle
					cx={center}
					cy={center}
					r={radius}
					fill="none"
					stroke={color}
					strokeWidth={GAUGE_STROKE_WIDTH}
					strokeDasharray={`${progress} ${circumference}`}
					strokeDashoffset={0}
					strokeLinecap="round"
					transform={`rotate(-90 ${center} ${center})`}
				/>
				<text
					x="50%"
					y="45%"
					textAnchor="middle"
					dominantBaseline="middle"
					className="ps-gauge-score"
				>
					{Math.round(score)}
				</text>
				<text
					x="50%"
					y="62%"
					textAnchor="middle"
					dominantBaseline="middle"
					className="ps-gauge-label"
					fill={color}
				>
					{label}
				</text>
			</svg>
		</div>
	);
}

function PredictiveScorePanel({ entityType, entityId, libraryId, onClose }) {
	const dispatch = useDispatch();
	const scoreDetail = useSelector(getScoreDetail);
	const availableKpis = useSelector(getAvailableKpis);
	const [selectedKpiIndex, setSelectedKpiIndex] = useState(0);

	useEffect(() => {
		if (entityId) {
			dispatch(loadScoreDetail(entityType, entityId));
		}
		setSelectedKpiIndex(0);
		return () => dispatch(clearScoreDetail());
	}, [dispatch, entityType, entityId]);

	// Load the library's KPI configs so score engine keys (e.g. "display-ctr") can be shown as their
	// friendly display names. Only fetched when not already loaded (e.g. from the library settings modal).
	useEffect(() => {
		if (libraryId && !availableKpis.length) {
			dispatch(loadLibraryKpis(libraryId));
		}
	}, [dispatch, libraryId, availableKpis.length]);

	const currentScore = scoreDetail?.scores?.[selectedKpiIndex] || scoreDetail?.scores?.[0] || null;

	// react-tooltip v4 binds listeners at rebuild() time. The KPI-info data-tip icon below only renders
	// once scoreDetail arrives — after the toolbar's mount-time rebuild — so its tooltip would never
	// attach. Rebuild whenever scoreDetail changes so the tooltip binds after the score renders.
	useTooltipRebuild(scoreDetail);

	useEffect(() => {
		if (currentScore && entityId) {
			trackScoreViewed(currentScore.score, currentScore.kpi, entityId);
		}
	}, [entityId, currentScore?.kpi]);

	// Kept above the early return so the hook order stays stable; null-safe for the empty case.
	// Show the friendly KPI name (e.g. "CTR (Click Through Rate)") instead of the raw engine key.
	const kpiOptions = useMemo(() => (scoreDetail?.scores || []).map((score, idx) => ({
		value: idx,
		label: resolveKpiName(score.kpi, availableKpis),
	})), [scoreDetail?.scores, availableKpis]);

	if (!scoreDetail || !scoreDetail.scores || scoreDetail.scores.length === 0) {
		return <div className="ps-panel ps-panel-empty">No score available</div>;
	}

	function handleKpiChange(option) {
		if (option && option.value !== selectedKpiIndex) {
			const fromKpi = scoreDetail.scores[selectedKpiIndex]?.kpi;
			trackKpiToggle(fromKpi, scoreDetail.scores[option.value]?.kpi);
			setSelectedKpiIndex(option.value);
		}
	}

	return (
		<div className="ps-panel">
			<div className="ps-header">
				<div className="ps-header-left">
					<h3 className="ps-title">PREDICTIVE SCORE</h3>
					{currentScore.scoredAt && (
						<span className="ps-timestamp">as of {moment(currentScore.scoredAt).format(DATE_AND_TIME_FORMAT)}</span>
					)}
				</div>
				{onClose && (
					<button type="button" className="ps-close" onClick={onClose}>
						<i className="fas fa-times" />
					</button>
				)}
			</div>

			<div className="ps-kpi-section">
				<label className="ps-kpi-label">KPI <i className="fas fa-info-circle ps-kpi-info" data-tip={KPI_TOOLTIP} data-place="top" /></label>
				<Select
					className="ps-kpi-select"
					value={kpiOptions[selectedKpiIndex]}
					onChange={handleKpiChange}
					options={kpiOptions}
					isSearchable={false}
					isClearable={false}
					components={SELECT_COMPONENTS}
				/>
			</div>

			<ScoreGauge score={currentScore.score} />

			<p className="ps-subtitle">
				{`${sentenceCaseLabel(getScoreLabel(currentScore.score))} creative performance expected`}
			</p>

			{currentScore.predictedPerformance && (
				<div className="ps-section">
					<h4>Overview</h4>
					<p>{currentScore.predictedPerformance}</p>
				</div>
			)}

			{currentScore.keyStrengths && currentScore.keyStrengths.length > 0 && (
				<div className="ps-section">
					<h4>Key Strengths</h4>
					{/* Items already arrive as full sentences with their own punctuation, so join with a space. */}
					<p>{currentScore.keyStrengths.join(" ")}</p>
				</div>
			)}

			{currentScore.potentialWeaknesses && currentScore.potentialWeaknesses.length > 0 && (
				<div className="ps-section">
					<h4>Potential Weaknesses</h4>
					<p>{currentScore.potentialWeaknesses.join(" ")}</p>
				</div>
			)}

			{currentScore.actionableSuggestions && currentScore.actionableSuggestions.length > 0 && (
				<div className="ps-recommendation">
					<h4><i className="fas fa-lightbulb ps-recommendation-icon" /> Recommendation</h4>
					{currentScore.actionableSuggestions.map(item => (
						<p key={item}>{item}</p>
					))}
				</div>
			)}

			<div className="ps-disclaimer">{DISCLAIMER_TEXT}</div>
		</div>
	);
}

PredictiveScorePanel.displayName = "PredictiveScorePanel";

export default PredictiveScorePanel;
